import { Dispatch, SetStateAction, SyntheticEvent, useEffect, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { Grid, TextField, Typography, Autocomplete } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { getPartners } from 'thunks/partnerThunk';
import { setIsLoading } from 'reducers/partnerReducer';
import { getPartnerName } from 'utils/stringUtils';
import useFilteredPartners from './hooks/useFilteredPartners';
import { usePartnerSearchStyles } from './hooks/usePartnerSearchStyles';
import { PartnerSearchResponse, ServiceRole } from 'pco-product-service-internal-consumer';
import { useThrottle } from 'components/hooks/useThrottle';
import { getServiceRoleStepMetadata } from 'utils/json-form/formDataUtils';

export interface Props {
  disabled?: boolean;
  handleChangeValidFrom?: (event: SyntheticEvent | Dayjs | null, value: string) => void;
  handleChangeValidTo?: (event: SyntheticEvent | Dayjs | null, value: string) => void;
  serviceRole?: ServiceRole;
  setRoleModified?: Dispatch<SetStateAction<boolean>>;
  partnerForServiceRole?: PartnerSearchResponse | null;
  setPartnerForServiceRole: Dispatch<SetStateAction<PartnerSearchResponse | null>>;
}

export const PartnerSearch = ({
  disabled = false,
  handleChangeValidTo,
  serviceRole,
  setRoleModified,
  handleChangeValidFrom,
  setPartnerForServiceRole,
  partnerForServiceRole,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [partnerSearchString, setPartnerSearchString] = useState<string>('');
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);

  const { partners, isLoading } = useSelector((state: RootState) => state.partner);
  const throttledInput = useThrottle(partnerSearchString, { wait: 700 });
  const filteredPartners = useFilteredPartners(partners, selectedServiceAgreement ?? null);
  const styles = usePartnerSearchStyles(partnerForServiceRole);

  const serviceRoleStepMetadata = selectedServiceAgreement
    ? getServiceRoleStepMetadata(selectedServiceAgreement)
    : undefined;
  const maximumInstances = serviceRoleStepMetadata?.serviceRoleList?.[0]?.maximumInstances;

  useEffect(
    function setPartnerForServiceRoleIfServiceRolePresentAndMaxInstancesOne() {
      if (serviceRole && !partnerForServiceRole && maximumInstances === 1) {
        setPartnerForServiceRole({
          fullName: serviceRole.fullName ?? '',
          globalPartnerId: serviceRole.partnerNumber ?? '',
          techId: serviceRole.partnerUid ?? '',
        });
      }
    },
    [serviceRole, partnerForServiceRole, partners, setPartnerForServiceRole, maximumInstances],
  );

  useEffect(() => {
    if (!disabled && throttledInput) {
      dispatch(setIsLoading(true));
      dispatch(getPartners(throttledInput));
    }
  }, [disabled, dispatch, throttledInput]);

  const handleChange = (event: SyntheticEvent, value: PartnerSearchResponse | null) => {
    if (!value) {
      handleChangeValidFrom?.(null, 'validFrom');
      handleChangeValidTo?.(null, 'validTo');
      setPartnerForServiceRole(null);
      setPartnerSearchString('');
    } else {
      setRoleModified?.(true);
      setPartnerForServiceRole(value);
      setPartnerSearchString('');
    }
  };

  const formatPartnerLabel = (option: PartnerSearchResponse) => {
    return `${option.fullName} ${option.globalPartnerId}`.trim();
  };

  return (
    <Grid container data-testid="partner-search" sx={{ display: 'flex', width: '100%' }}>
      <Typography
        sx={{
          pl: 1,
          mb: 0.5,
          ml: 0.5,
          color: partnerForServiceRole ? 'black' : 'red',
        }}
        color="primary"
        variant="body2"
      >
        {partnerForServiceRole ? 'Partner Name or no.' : 'Partner Name or no.*'}
      </Typography>
      <Grid
        container
        sx={{
          ml: 2.3,
          mb: 1.5,
          display: 'flex',
          justifyItems: 'center',
          alignItems: 'center',
        }}
      >
        {!disabled ? (
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={filteredPartners}
            loading={isLoading}
            value={
              serviceRole
                ? partners.filter((partner) => partner.globalPartnerId === serviceRole?.partnerNumber)[0]
                : partnerForServiceRole
            }
            isOptionEqualToValue={(option, value) => getPartnerName(option) === getPartnerName(value)}
            getOptionLabel={formatPartnerLabel}
            onChange={handleChange}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!partnerForServiceRole}
                helperText={!partnerForServiceRole || !serviceRole ? 'Please select a partner' : ''}
                onChange={(e) => setPartnerSearchString(e.target.value.trim())}
                sx={styles}
              />
            )}
          />
        ) : (
          <TextField
            disabled
            defaultValue={
              serviceRole &&
              formatPartnerLabel({
                fullName: serviceRole.fullName ?? '',
                globalPartnerId: serviceRole.partnerNumber ?? '',
              } as PartnerSearchResponse)
            }
            sx={styles}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default PartnerSearch;
